import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDate } from '../lib/utils';
import { compressImageToBase64 } from '../lib/imageCompress';
import {
  Plus,
  Search,
  Users,
  TrendingUp,
  TrendingDown,
  Wallet,
  Calendar,
  Receipt,
  Upload,
  Trash2,
  Edit2,
  Eye,
  Download,
  Filter,
  CheckSquare,
  Square,
  X,
  Settings,
  FileText,
  Wand2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import ExportButton from '../components/ExportButton';
import FinanceOverviewCards from '../components/sponsorship/FinanceOverviewCards';
import ChildLedgerTab from '../components/sponsorship/ChildLedgerTab';
import SponsorStatementsTab from '../components/sponsorship/SponsorStatementsTab';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const todayStr = () => new Date().toISOString().slice(0, 10);

const SponsorshipAccountabilityPage = () => {
  const { hasRole } = useAuth();
  const canEdit = hasRole(['admin', 'finance_officer', 'school_head', 'project_manager']);

  // Core data
  const [children, setChildren] = useState([]);
  const [dashboard, setDashboard] = useState({ rows: [], totals: {} });
  const [categories, setCategories] = useState([]);
  const [terms, setTerms] = useState([]);
  const [batches, setBatches] = useState([]);
  const [expensesV2, setExpensesV2] = useState([]);
  const [plannedExpenses, setPlannedExpenses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [classFilter, setClassFilter] = useState('all');
  const [termFilter, setTermFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('dashboard');

  // Selection (spreadsheet style)
  const [selected, setSelected] = useState(new Set());

  // Dialogs
  const [bulkDonationOpen, setBulkDonationOpen] = useState(false);
  const [editBatch, setEditBatch] = useState(null);
  const [deleteBatchTarget, setDeleteBatchTarget] = useState(null);
  const [bulkExpenseOpen, setBulkExpenseOpen] = useState(false);
  const [plannedOpen, setPlannedOpen] = useState(false);
  const [categoryMgrOpen, setCategoryMgrOpen] = useState(false);
  const [termMgrOpen, setTermMgrOpen] = useState(false);
  const [childProfileId, setChildProfileId] = useState(null);

  const loadDashboard = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (termFilter !== 'all') params.append('term_id', termFilter);
      params.append('active_only', statusFilter !== 'all' ? 'true' : 'false');
      const res = await axios.get(`${API_URL}/api/sponsorship/accountability/dashboard?${params}`);
      setDashboard(res.data);
    } catch (err) {
      toast.error('Failed to load dashboard');
    }
  }, [termFilter, statusFilter]);

  const loadCore = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, catRes, tRes, bRes, eRes, pRes] = await Promise.all([
        axios.get(`${API_URL}/api/sponsorship/children`),
        axios.get(`${API_URL}/api/sponsorship/categories`),
        axios.get(`${API_URL}/api/sponsorship/terms`),
        axios.get(`${API_URL}/api/sponsorship/donation-batches`),
        axios.get(`${API_URL}/api/sponsorship/expenses-v2`),
        axios.get(`${API_URL}/api/sponsorship/planned-expenses`),
      ]);
      setChildren(cRes.data);
      setCategories(catRes.data);
      setTerms(tRes.data);
      setBatches(bRes.data);
      setExpensesV2(eRes.data);
      setPlannedExpenses(pRes.data);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCore(); }, [loadCore]);
  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const classes = useMemo(() => {
    return Array.from(new Set(dashboard.rows.map((r) => r.class).filter(Boolean))).sort();
  }, [dashboard.rows]);

  // Memoize the active-categories list so the two <Select> dropdowns don't
  // re-filter on every render.
  const activeCategories = useMemo(
    () => categories.filter((c) => c.active_status),
    [categories],
  );

  const filteredRows = useMemo(() => {
    return dashboard.rows.filter((r) => {
      if (statusFilter !== 'all' && r.sponsorship_status !== statusFilter) return false;
      if (classFilter !== 'all' && r.class !== classFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!(r.name || '').toLowerCase().includes(q) &&
            !(r.sponsor_name || '').toLowerCase().includes(q) &&
            !(r.class || '').toLowerCase().includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [dashboard.rows, statusFilter, classFilter, search]);

  const toggleRow = (childId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(childId)) next.delete(childId);
      else next.add(childId);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === filteredRows.length && filteredRows.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filteredRows.map((r) => r.child_id)));
    }
  };

  const downloadChildPdf = async (childId) => {
    try {
      const res = await axios.get(`${API_URL}/api/exports/child-accountability/${childId}`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `child_accountability_${childId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download report');
    }
  };

  const balanceClass = (n) =>
    n > 0 ? 'text-green-600' : n < 0 ? 'text-red-600' : 'text-muted-foreground';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96 text-muted-foreground">
        Loading sponsorship accountability…
      </div>
    );
  }

  const totals = dashboard.totals || {};

  return (
    <div className="space-y-4" data-testid="sponsorship-accountability-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
            <Wallet className="h-7 w-7 text-emerald-600" />
            Sponsorship Accountability
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Spreadsheet-style operational dashboard · bulk donations · expense allocations · forecast balances
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ExportButton path="exports/list/sponsored-children" filenameBase="suubi_children" label="Export Children" testId="export-children" />
          <ExportButton path="exports/list/sponsorship-expenses" filenameBase="suubi_sponsor_expenses" label="Export Expenses" />
          <Button variant="outline" onClick={() => setCategoryMgrOpen(true)} data-testid="manage-categories-btn">
            <Settings className="h-4 w-4 mr-1" /> Categories
          </Button>
          <Button variant="outline" onClick={() => setTermMgrOpen(true)} data-testid="manage-terms-btn">
            <Calendar className="h-4 w-4 mr-1" /> Terms
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-3">
        <Card>
          <CardHeader className="pb-2 p-3 md:p-6">
            <CardDescription className="text-xs flex items-center gap-1"><Users className="h-3 w-3" /> Children</CardDescription>
            <CardTitle className="text-xl md:text-2xl">{dashboard.child_count || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2 p-3 md:p-6">
            <CardDescription className="text-xs flex items-center gap-1 text-green-600"><TrendingUp className="h-3 w-3" /> Donations</CardDescription>
            <CardTitle className="text-base md:text-xl text-green-600">{formatCurrency(totals.donations || 0)}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2 p-3 md:p-6">
            <CardDescription className="text-xs flex items-center gap-1 text-red-600"><TrendingDown className="h-3 w-3" /> Actuals</CardDescription>
            <CardTitle className="text-base md:text-xl text-red-600">{formatCurrency(totals.actuals || 0)}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2 p-3 md:p-6">
            <CardDescription className="text-xs flex items-center gap-1 text-amber-600"><Calendar className="h-3 w-3" /> Planned</CardDescription>
            <CardTitle className="text-base md:text-xl text-amber-600">{formatCurrency(totals.planned || 0)}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2 p-3 md:p-6">
            <CardDescription className="text-xs flex items-center gap-1">Forecast</CardDescription>
            <CardTitle className={cn('text-base md:text-xl', balanceClass(totals.forecast_balance || 0))}>
              {formatCurrency(totals.forecast_balance || 0)}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      <FinanceOverviewCards refreshKey={activeTab} />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-child-ledger">Child Ledger</TabsTrigger>
          <TabsTrigger value="statements" data-testid="tab-sponsor-statements">Sponsor Statements</TabsTrigger>
          <TabsTrigger value="batches" data-testid="tab-batches">Donation Batches</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expensesv2">Expenses & Allocations</TabsTrigger>
          <TabsTrigger value="planned" data-testid="tab-planned">Planned Expenses</TabsTrigger>
        </TabsList>

        {/* DASHBOARD */}
        <TabsContent value="ledger" className="mt-4">
          <ChildLedgerTab onChanged={() => setActiveTab('ledger')} />
        </TabsContent>

        <TabsContent value="statements" className="mt-4">
          <SponsorStatementsTab />
        </TabsContent>

        <TabsContent value="dashboard" className="mt-4 space-y-3">
          <Card>
            <CardContent className="p-3 flex flex-wrap gap-2 items-center">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, class, or sponsor…"
                  className="pl-10"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="dashboard-search"
                />
              </div>
              <Select value={termFilter} onValueChange={setTermFilter}>
                <SelectTrigger className="w-44" data-testid="term-filter"><SelectValue placeholder="Term" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All terms</SelectItem>
                  {terms.map((t) => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={classFilter} onValueChange={setClassFilter}>
                <SelectTrigger className="w-32" data-testid="class-filter"><SelectValue placeholder="Class" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All classes</SelectItem>
                  {classes.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-32" data-testid="status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
              {canEdit && (
                <>
                  <Button
                    onClick={() => setBulkDonationOpen(true)}
                    disabled={selected.size === 0}
                    data-testid="bulk-donate-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Bulk Donation {selected.size > 0 && `(${selected.size})`}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => setBulkExpenseOpen(true)}
                    disabled={selected.size === 0}
                    data-testid="bulk-expense-btn"
                  >
                    <Receipt className="h-4 w-4 mr-1" />
                    Allocate Expense {selected.size > 0 && `(${selected.size})`}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          {selected.size > 0 && (
            <div className="rounded-lg border bg-primary/5 px-3 py-2 text-sm flex items-center justify-between" data-testid="selection-bar">
              <span><b>{selected.size}</b> child{selected.size !== 1 ? 'ren' : ''} selected</span>
              <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
                <X className="h-3 w-3 mr-1" /> Clear
              </Button>
            </div>
          )}

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table className="text-sm">
                  <TableHeader className="sticky top-0 bg-background z-10">
                    <TableRow>
                      <TableHead className="w-8">
                        <button onClick={toggleAll} className="p-0.5" data-testid="select-all-btn">
                          {selected.size === filteredRows.length && filteredRows.length > 0
                            ? <CheckSquare className="h-4 w-4 text-primary" />
                            : <Square className="h-4 w-4 text-muted-foreground" />}
                        </button>
                      </TableHead>
                      <TableHead>Child</TableHead>
                      <TableHead>Class</TableHead>
                      <TableHead className="text-right">Donations</TableHead>
                      <TableHead className="text-right">Actual</TableHead>
                      <TableHead className="text-right">Planned</TableHead>
                      <TableHead className="text-right">Current</TableHead>
                      <TableHead className="text-right">Forecast</TableHead>
                      <TableHead className="text-right w-24">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-10 text-muted-foreground">
                          No children match the current filters.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredRows.map((r) => (
                        <TableRow
                          key={r.child_id}
                          className={cn(selected.has(r.child_id) && 'bg-primary/5')}
                          data-testid={`row-${r.child_id}`}
                        >
                          <TableCell>
                            <button onClick={() => toggleRow(r.child_id)} className="p-0.5">
                              {selected.has(r.child_id)
                                ? <CheckSquare className="h-4 w-4 text-primary" />
                                : <Square className="h-4 w-4 text-muted-foreground" />}
                            </button>
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{r.name}</div>
                            {r.sponsor_name && <div className="text-xs text-muted-foreground">Sponsor: {r.sponsor_name}</div>}
                          </TableCell>
                          <TableCell>{r.class || '—'}</TableCell>
                          <TableCell className="text-right text-green-600">{formatCurrency(r.donations_received)}</TableCell>
                          <TableCell className="text-right text-red-600">{formatCurrency(r.actual_expenses)}</TableCell>
                          <TableCell className="text-right text-amber-600">{formatCurrency(r.planned_expenses)}</TableCell>
                          <TableCell className={cn('text-right font-semibold', balanceClass(r.current_balance))}>{formatCurrency(r.current_balance)}</TableCell>
                          <TableCell className={cn('text-right font-bold', balanceClass(r.forecast_balance))}>{formatCurrency(r.forecast_balance)}</TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="icon" onClick={() => setChildProfileId(r.child_id)} title="View profile" data-testid={`view-profile-${r.child_id}`}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => downloadChildPdf(r.child_id)} title="Download PDF report" data-testid={`download-pdf-${r.child_id}`}>
                              <FileText className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* BATCHES */}
        <TabsContent value="batches" className="mt-4 space-y-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
              <div>
                <CardTitle>Donation Batches</CardTitle>
                <CardDescription>{batches.length} batch{batches.length !== 1 ? 'es' : ''}</CardDescription>
              </div>
              {canEdit && (
                <Button onClick={() => { setEditBatch(null); setBulkDonationOpen(true); }}>
                  <Plus className="h-4 w-4 mr-1" /> New Batch
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {batches.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">No batches yet</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Batch</TableHead>
                      <TableHead>Payment Date</TableHead>
                      <TableHead className="text-right">Default Amount</TableHead>
                      <TableHead className="text-right">Lines</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead>Created By</TableHead>
                      {canEdit && <TableHead className="text-right w-44"></TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {batches.map((b) => (
                      <TableRow key={b.id} data-testid={`batch-row-${b.id}`}>
                        <TableCell>
                          <div className="font-medium">{b.batch_name}</div>
                          {b.notes && <div className="text-xs text-muted-foreground">{b.notes}</div>}
                        </TableCell>
                        <TableCell>{formatDate(b.payment_date)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(b.default_amount)}</TableCell>
                        <TableCell className="text-right">{b.line_count}</TableCell>
                        <TableCell className="text-right font-bold text-green-600">{formatCurrency(b.total_amount)}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{b.created_by || '—'}</TableCell>
                        {canEdit && (
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <Button size="sm" variant="outline" className="h-8 px-2.5" onClick={() => { setEditBatch(b); setBulkDonationOpen(true); }} data-testid={`edit-batch-${b.id}`}>
                                <Edit2 className="h-3.5 w-3.5 mr-1" /> Edit
                              </Button>
                              <Button size="sm" variant="outline" className="h-8 px-2.5 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setDeleteBatchTarget(b)} data-testid={`delete-batch-${b.id}`}>
                                <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* EXPENSES V2 */}
        <TabsContent value="expenses" className="mt-4 space-y-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
              <div>
                <CardTitle>Expenses & Allocations</CardTitle>
                <CardDescription>{expensesV2.length} expense record{expensesV2.length !== 1 ? 's' : ''} (one expense → many children)</CardDescription>
              </div>
              {canEdit && (
                <Button onClick={() => setBulkExpenseOpen(true)}>
                  <Receipt className="h-4 w-4 mr-1" /> Allocate Expense
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {expensesV2.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">No expenses logged via the accountability module yet</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Vendor</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Children</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expensesV2.map((e) => (
                      <TableRow key={e.id}>
                        <TableCell>{formatDate(e.expense_date)}</TableCell>
                        <TableCell>{e.category_name || '—'}</TableCell>
                        <TableCell>{e.vendor || '—'}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(e.total_amount)}</TableCell>
                        <TableCell className="text-right">{(e.allocations || []).length}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{e.notes || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* PLANNED */}
        <TabsContent value="planned" className="mt-4 space-y-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
              <div>
                <CardTitle>Planned Expenses</CardTitle>
                <CardDescription>Forecast items not yet incurred</CardDescription>
              </div>
              {canEdit && (
                <Button onClick={() => setPlannedOpen(true)} data-testid="add-planned-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add Planned
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {plannedExpenses.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">No planned expenses yet</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Child</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Estimate</TableHead>
                      <TableHead>Expected Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {plannedExpenses.map((p) => {
                      const child = children.find((c) => c.id === p.child_id);
                      return (
                        <TableRow key={p.id}>
                          <TableCell>{child?.name || '—'}</TableCell>
                          <TableCell>{p.category_name || '—'}</TableCell>
                          <TableCell className="text-right text-amber-600">{formatCurrency(p.estimated_amount)}</TableCell>
                          <TableCell>{p.expected_date ? formatDate(p.expected_date) : '—'}</TableCell>
                          <TableCell>
                            <Badge variant={p.status === 'converted' ? 'secondary' : 'outline'}>{p.status}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {canEdit && p.status !== 'converted' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  title="Convert to actual expense"
                                  onClick={async () => {
                                    try {
                                      await axios.post(`${API_URL}/api/sponsorship/planned-expenses/${p.id}/convert`);
                                      toast.success('Converted to actual expense');
                                      loadCore();
                                      loadDashboard();
                                    } catch (e) { toast.error('Failed to convert'); }
                                  }}
                                >
                                  <Wand2 className="h-4 w-4 text-emerald-600" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={async () => {
                                    if (!window.confirm('Delete this planned expense?')) return;
                                    try {
                                      await axios.delete(`${API_URL}/api/sponsorship/planned-expenses/${p.id}`);
                                      toast.success('Deleted');
                                      loadCore();
                                      loadDashboard();
                                    } catch (e) { toast.error('Failed to delete'); }
                                  }}
                                >
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Bulk Donation Dialog */}
      {bulkDonationOpen && (
        <BulkDonationDialog
          open={bulkDonationOpen}
          onClose={() => { setBulkDonationOpen(false); setEditBatch(null); }}
          children={editBatch ? children : children.filter((c) => selected.size === 0 || selected.has(c.id))}
          terms={terms}
          batch={editBatch}
          onSubmitted={() => { loadCore(); loadDashboard(); setSelected(new Set()); }}
        />
      )}

      {/* Delete batch confirm */}
      <AlertDialog open={!!deleteBatchTarget} onOpenChange={(o) => { if (!o) setDeleteBatchTarget(null); }}>
        <AlertDialogContent data-testid="delete-batch-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete donation batch?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteBatchTarget ? (
                <>Delete <strong>{deleteBatchTarget.batch_name}</strong> and all {deleteBatchTarget.line_count} donation line{deleteBatchTarget.line_count !== 1 ? 's' : ''} in it? Child balances derived from this batch will change. This cannot be undone.</>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-batch-confirm"
              onClick={async () => {
                try {
                  await axios.delete(`${API_URL}/api/sponsorship/donation-batches/${deleteBatchTarget.id}`);
                  toast.success('Batch deleted');
                  setDeleteBatchTarget(null);
                  loadCore(); loadDashboard();
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Failed to delete batch');
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Expense Dialog */}
      {bulkExpenseOpen && (
        <BulkExpenseDialog
          open={bulkExpenseOpen}
          onClose={() => setBulkExpenseOpen(false)}
          children={children.filter((c) => selected.size === 0 || selected.has(c.id))}
          categories={categories}
          terms={terms}
          onSubmitted={() => { loadCore(); loadDashboard(); setSelected(new Set()); }}
        />
      )}

      {/* Planned Expense Dialog */}
      {plannedOpen && (
        <PlannedExpenseDialog
          open={plannedOpen}
          onClose={() => setPlannedOpen(false)}
          children={children}
          categories={categories}
          terms={terms}
          onSubmitted={() => { loadCore(); loadDashboard(); }}
        />
      )}

      {/* Categories Manager */}
      {categoryMgrOpen && (
        <CategoryManagerDialog
          open={categoryMgrOpen}
          onClose={() => setCategoryMgrOpen(false)}
          categories={categories}
          onChanged={loadCore}
          canEdit={canEdit}
        />
      )}

      {/* Terms Manager */}
      {termMgrOpen && (
        <TermManagerDialog
          open={termMgrOpen}
          onClose={() => setTermMgrOpen(false)}
          terms={terms}
          onChanged={loadCore}
          canEdit={canEdit}
        />
      )}

      {/* Child Profile (read-only) */}
      {childProfileId && (
        <ChildProfileDialog
          childId={childProfileId}
          onClose={() => setChildProfileId(null)}
          onDownload={() => downloadChildPdf(childProfileId)}
        />
      )}
    </div>
  );
};

// ============================================================================
// BULK DONATION DIALOG
// ============================================================================
const BulkDonationDialog = ({ open, onClose, children, terms, onSubmitted, batch }) => {
  const [batchName, setBatchName] = useState(batch?.batch_name || '');
  const [defaultAmount, setDefaultAmount] = useState(batch ? batch.default_amount : 100000);
  const [termId, setTermId] = useState(batch?.term_id || 'none');
  const [paymentDate, setPaymentDate] = useState(batch?.payment_date || todayStr());
  const [notes, setNotes] = useState(batch?.notes || '');
  const [lines, setLines] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (batch) return; // edit mode: lines come from the existing batch
    const init = {};
    children.forEach((c) => { init[c.id] = defaultAmount; });
    setLines(init);
  }, [children, defaultAmount, batch]);

  useEffect(() => {
    if (!batch) return;
    axios.get(`${API_URL}/api/sponsorship/donation-batches/${batch.id}`).then((r) => {
      const init = {};
      children.forEach((c) => { init[c.id] = 0; });
      (r.data.donations || []).forEach((d) => { init[d.child_id] = d.amount; });
      setLines(init);
    }).catch(() => toast.error('Failed to load batch lines'));
  }, [batch, children]);

  const grandTotal = Object.values(lines).reduce((s, v) => s + (Number(v) || 0), 0);

  const submit = async () => {
    if (!batchName) { toast.error('Batch name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        batch: {
          batch_name: batchName,
          default_amount: Number(defaultAmount) || 0,
          term_id: termId === 'none' ? null : termId,
          payment_date: paymentDate,
          notes,
        },
        lines: Object.entries(lines).map(([child_id, amount]) => ({ child_id, amount: Number(amount) || 0 })),
      };
      if (batch) {
        await axios.put(`${API_URL}/api/sponsorship/donation-batches/${batch.id}`, payload);
        toast.success(`Batch updated (${grandTotal.toLocaleString()} UGX)`);
      } else {
        await axios.post(`${API_URL}/api/sponsorship/donation-batches`, payload);
        toast.success(`Batch saved (${grandTotal.toLocaleString()} UGX)`);
      }
      onSubmitted();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save batch');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{batch ? `Edit Batch — ${batch.batch_name}` : 'Bulk Donation Entry'}</DialogTitle>
          <DialogDescription>{batch ? 'Update batch details and per-child amounts. Lines are replaced on save.' : 'Enter a sponsorship batch. Default amount auto-fills; edit cells for exceptions, set 0 to skip.'}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 grid-cols-2">
          <div className="space-y-1 col-span-2">
            <Label>Batch Name *</Label>
            <Input
              value={batchName}
              onChange={(e) => setBatchName(e.target.value)}
              placeholder="e.g. June 2026 Sponsorship Payments"
              data-testid="batch-name-input"
            />
          </div>
          <div className="space-y-1">
            <Label>Default Amount (UGX)</Label>
            <Input
              type="number"
              value={defaultAmount}
              onChange={(e) => setDefaultAmount(e.target.value)}
              data-testid="default-amount-input"
            />
          </div>
          <div className="space-y-1">
            <Label>Payment Date</Label>
            <Input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Term</Label>
            <Select value={termId} onValueChange={setTermId}>
              <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {terms.map((t) => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Notes</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <div className="border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Child</TableHead>
                <TableHead>Class</TableHead>
                <TableHead className="text-right">Amount (UGX)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {children.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell>{c.grade || '—'}</TableCell>
                  <TableCell className="text-right">
                    <Input
                      type="number"
                      value={lines[c.id] ?? 0}
                      onChange={(e) => setLines((prev) => ({ ...prev, [c.id]: e.target.value }))}
                      className="w-32 text-right ml-auto"
                      data-testid={`amount-${c.id}`}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="text-right text-lg">
          <span className="text-muted-foreground mr-2">Total:</span>
          <span className="font-bold text-green-600">{formatCurrency(grandTotal)}</span>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="submit-batch-btn">
            {saving ? 'Saving…' : 'Save Batch'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// BULK EXPENSE DIALOG
// ============================================================================
const BulkExpenseDialog = ({ open, onClose, children, categories, terms, onSubmitted }) => {
  const activeCategories = (categories || []).filter((c) => c.active_status);
  const [categoryName, setCategoryName] = useState('school_uniform');
  const [vendor, setVendor] = useState('');
  const [totalAmount, setTotalAmount] = useState(0);
  const [expenseDate, setExpenseDate] = useState(todayStr());
  const [termId, setTermId] = useState('none');
  const [notes, setNotes] = useState('');
  const [receipt, setReceipt] = useState('');
  const [splitMode, setSplitMode] = useState('equal'); // equal | manual
  const [allocs, setAllocs] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (splitMode === 'equal' && children.length > 0) {
      const per = Math.round((Number(totalAmount) || 0) / children.length);
      const init = {};
      children.forEach((c) => { init[c.id] = per; });
      setAllocs(init);
    }
  }, [splitMode, totalAmount, children]);

  const allocTotal = Object.values(allocs).reduce((s, v) => s + (Number(v) || 0), 0);

  const handleReceipt = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Max 10MB'); return; }
    try {
      const compressed = await compressImageToBase64(file, { maxDimension: 1280, quality: 0.75 });
      setReceipt(compressed);
      toast.success('Receipt compressed and attached');
    } catch { toast.error('Failed to attach receipt'); }
  };

  const submit = async () => {
    if (!totalAmount || Number(totalAmount) <= 0) { toast.error('Total amount required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/sponsorship/expenses-v2`, {
        expense: {
          category_name: categoryName,
          vendor,
          total_amount: Number(totalAmount),
          expense_date: expenseDate,
          term_id: termId === 'none' ? null : termId,
          receipt_image: receipt || null,
          notes,
        },
        allocations: Object.entries(allocs)
          .filter(([, v]) => Number(v) > 0)
          .map(([child_id, allocated_amount]) => ({
            child_id, allocated_amount: Number(allocated_amount),
          })),
      });
      toast.success(`Expense saved (${Object.values(allocs).filter((v) => Number(v) > 0).length} allocations)`);
      onSubmitted();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Allocate Expense to Many Children</DialogTitle>
          <DialogDescription>One expense record · one receipt · allocate different amounts per child.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 grid-cols-2">
          <div className="space-y-1">
            <Label>Category</Label>
            <Select value={categoryName} onValueChange={setCategoryName}>
              <SelectTrigger data-testid="expense-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {activeCategories.map((c) => (
                  <SelectItem key={c.id} value={c.name}>{c.name.replace(/_/g, ' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Vendor / Supplier</Label>
            <Input value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="e.g. Kampala Tailors" />
          </div>
          <div className="space-y-1">
            <Label>Total Amount (UGX)</Label>
            <Input
              type="number"
              value={totalAmount}
              onChange={(e) => setTotalAmount(e.target.value)}
              data-testid="total-amount-input"
            />
          </div>
          <div className="space-y-1">
            <Label>Expense Date</Label>
            <Input type="date" value={expenseDate} onChange={(e) => setExpenseDate(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Term</Label>
            <Select value={termId} onValueChange={setTermId}>
              <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {terms.map((t) => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Split Mode</Label>
            <Select value={splitMode} onValueChange={setSplitMode}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="equal">Equal split</SelectItem>
                <SelectItem value="manual">Manual amounts</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 col-span-2">
            <Label>Receipt Image</Label>
            <div className="flex items-center gap-2">
              <Input type="file" accept="image/*" onChange={handleReceipt} />
              {receipt && <Badge variant="outline">Attached</Badge>}
            </div>
          </div>
          <div className="space-y-1 col-span-2">
            <Label>Notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <div className="border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Child</TableHead>
                <TableHead>Class</TableHead>
                <TableHead className="text-right">Allocated (UGX)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {children.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell>{c.grade || '—'}</TableCell>
                  <TableCell className="text-right">
                    <Input
                      type="number"
                      value={allocs[c.id] ?? 0}
                      onChange={(e) => setAllocs((prev) => ({ ...prev, [c.id]: e.target.value }))}
                      className="w-32 text-right ml-auto"
                      data-testid={`alloc-${c.id}`}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between">
          <div className={cn(
            'text-sm',
            Math.abs(allocTotal - Number(totalAmount)) < 1 ? 'text-green-600' : 'text-amber-600',
          )}>
            Allocated: <b>{formatCurrency(allocTotal)}</b> / Total: <b>{formatCurrency(Number(totalAmount) || 0)}</b>
            {Math.abs(allocTotal - Number(totalAmount)) >= 1 && ' (mismatch — adjust amounts)'}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="submit-expense-btn">
            {saving ? 'Saving…' : 'Save Expense + Allocations'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// PLANNED EXPENSE DIALOG
// ============================================================================
const PlannedExpenseDialog = ({ open, onClose, children, categories, terms, onSubmitted }) => {
  const activeCategories = (categories || []).filter((c) => c.active_status);
  const [form, setForm] = useState({
    child_id: '',
    category_name: 'school_fees',
    estimated_amount: 0,
    expected_term_id: 'none',
    expected_date: todayStr(),
    notes: '',
  });
  const submit = async () => {
    if (!form.child_id) { toast.error('Pick a child'); return; }
    try {
      const payload = { ...form, expected_term_id: form.expected_term_id === 'none' ? null : form.expected_term_id };
      await axios.post(`${API_URL}/api/sponsorship/planned-expenses`, payload);
      toast.success('Planned expense added');
      onSubmitted();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Planned Expense</DialogTitle>
          <DialogDescription>Forecast item — affects forecast balance but not actual.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Child *</Label>
            <Select value={form.child_id} onValueChange={(v) => setForm({ ...form, child_id: v })}>
              <SelectTrigger data-testid="planned-child-select"><SelectValue placeholder="Pick a child" /></SelectTrigger>
              <SelectContent>
                {children.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name} ({c.grade || '—'})</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Category</Label>
              <Select value={form.category_name} onValueChange={(v) => setForm({ ...form, category_name: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {activeCategories.map((c) => (
                    <SelectItem key={c.id} value={c.name}>{c.name.replace(/_/g, ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Estimate (UGX)</Label>
              <Input type="number" value={form.estimated_amount} onChange={(e) => setForm({ ...form, estimated_amount: Number(e.target.value) })} data-testid="planned-amount-input" />
            </div>
            <div className="space-y-1">
              <Label>Expected Date</Label>
              <Input type="date" value={form.expected_date} onChange={(e) => setForm({ ...form, expected_date: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Term</Label>
              <Select value={form.expected_term_id} onValueChange={(v) => setForm({ ...form, expected_term_id: v })}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {terms.map((t) => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label>Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} data-testid="submit-planned-btn">Add</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// CATEGORY MANAGER
// ============================================================================
const CategoryManagerDialog = ({ open, onClose, categories, onChanged, canEdit }) => {
  const [newName, setNewName] = useState('');

  const add = async () => {
    if (!newName) return;
    try {
      await axios.post(`${API_URL}/api/sponsorship/categories`, { name: newName, type: 'expense', active_status: true });
      toast.success('Added');
      setNewName('');
      onChanged();
    } catch (e) { toast.error('Failed'); }
  };
  const archive = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/sponsorship/categories/${id}`);
      toast.success('Archived');
      onChanged();
    } catch (e) { toast.error('Failed'); }
  };
  const restore = async (cat) => {
    try {
      await axios.put(`${API_URL}/api/sponsorship/categories/${cat.id}`, { ...cat, active_status: true });
      toast.success('Restored');
      onChanged();
    } catch (e) { toast.error('Failed'); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage Expense Categories</DialogTitle>
          <DialogDescription>Add, archive, or restore — no developer needed.</DialogDescription>
        </DialogHeader>
        {canEdit && (
          <div className="flex gap-2">
            <Input
              placeholder="new_category_name"
              value={newName}
              onChange={(e) => setNewName(e.target.value.replace(/\s+/g, '_').toLowerCase())}
              data-testid="new-category-input"
            />
            <Button onClick={add} data-testid="add-category-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
          </div>
        )}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Status</TableHead>
              {canEdit && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {categories.map((c) => (
              <TableRow key={c.id}>
                <TableCell className={!c.active_status ? 'text-muted-foreground line-through' : 'font-medium'}>
                  {c.name.replace(/_/g, ' ')}
                </TableCell>
                <TableCell>
                  <Badge variant={c.active_status ? 'default' : 'secondary'}>
                    {c.active_status ? 'active' : 'archived'}
                  </Badge>
                </TableCell>
                {canEdit && (
                  <TableCell className="text-right">
                    {c.active_status
                      ? <Button variant="ghost" size="sm" onClick={() => archive(c.id)}>Archive</Button>
                      : <Button variant="ghost" size="sm" onClick={() => restore(c)}>Restore</Button>}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// TERM MANAGER
// ============================================================================
const TermManagerDialog = ({ open, onClose, terms, onChanged, canEdit }) => {
  const [form, setForm] = useState({ name: '', start_date: '', end_date: '', active_status: true });
  const add = async () => {
    if (!form.name || !form.start_date || !form.end_date) { toast.error('All fields required'); return; }
    try {
      await axios.post(`${API_URL}/api/sponsorship/terms`, form);
      setForm({ name: '', start_date: '', end_date: '', active_status: true });
      toast.success('Term added');
      onChanged();
    } catch (e) { toast.error('Failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this term?')) return;
    try {
      await axios.delete(`${API_URL}/api/sponsorship/terms/${id}`);
      toast.success('Deleted');
      onChanged();
    } catch (e) { toast.error('Failed'); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Manage Terms</DialogTitle>
          <DialogDescription>Academic terms for filtering and reports.</DialogDescription>
        </DialogHeader>
        {canEdit && (
          <div className="grid grid-cols-4 gap-2 items-end">
            <div className="col-span-2 space-y-1">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Term 2, 2026" data-testid="term-name-input" />
            </div>
            <div className="space-y-1">
              <Label>Start</Label>
              <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>End</Label>
              <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
            </div>
            <Button onClick={add} className="col-span-4" data-testid="add-term-btn"><Plus className="h-4 w-4 mr-1" /> Add Term</Button>
          </div>
        )}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Start</TableHead>
              <TableHead>End</TableHead>
              {canEdit && <TableHead></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {terms.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-4">No terms yet</TableCell></TableRow>
            ) : terms.map((t) => (
              <TableRow key={t.id}>
                <TableCell className="font-medium">{t.name}</TableCell>
                <TableCell>{formatDate(t.start_date)}</TableCell>
                <TableCell>{formatDate(t.end_date)}</TableCell>
                {canEdit && (
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => del(t.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// CHILD PROFILE DIALOG (secondary view — history + download)
// ============================================================================
const ChildProfileDialog = ({ childId, onClose, onDownload }) => {
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/api/sponsorship/children/${childId}/profile`)
      .then((r) => setProfile(r.data))
      .catch(() => toast.error('Failed to load profile'));
  }, [childId]);
  if (!profile) {
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent>Loading profile…</DialogContent>
      </Dialog>
    );
  }
  const child = profile.child || {};
  const sum = profile.summary || {};
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="child-profile-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{child.name}</span>
            <Button size="sm" onClick={onDownload}><Download className="h-4 w-4 mr-1" /> PDF Report</Button>
          </DialogTitle>
          <DialogDescription>
            {child.grade || '—'} · {child.school || '—'} · Sponsor: {child.sponsor_name || '—'}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <Card><CardHeader className="p-3 pb-2"><CardDescription className="text-xs">Donations</CardDescription><CardTitle className="text-green-600 text-base">{formatCurrency(sum.total_donations)}</CardTitle></CardHeader></Card>
          <Card><CardHeader className="p-3 pb-2"><CardDescription className="text-xs">Actuals</CardDescription><CardTitle className="text-red-600 text-base">{formatCurrency(sum.total_actual_expenses)}</CardTitle></CardHeader></Card>
          <Card><CardHeader className="p-3 pb-2"><CardDescription className="text-xs">Planned</CardDescription><CardTitle className="text-amber-600 text-base">{formatCurrency(sum.total_planned_expenses)}</CardTitle></CardHeader></Card>
          <Card><CardHeader className="p-3 pb-2"><CardDescription className="text-xs">Current</CardDescription><CardTitle className="text-base">{formatCurrency(sum.current_balance)}</CardTitle></CardHeader></Card>
          <Card><CardHeader className="p-3 pb-2"><CardDescription className="text-xs">Forecast</CardDescription><CardTitle className="text-base">{formatCurrency(sum.forecast_balance)}</CardTitle></CardHeader></Card>
        </div>

        <Tabs defaultValue="don">
          <TabsList>
            <TabsTrigger value="don">Donations ({profile.donations.length})</TabsTrigger>
            <TabsTrigger value="exp">Expenses ({profile.allocations.length + (profile.legacy_expenses||[]).length})</TabsTrigger>
            <TabsTrigger value="plan">Planned ({profile.planned_expenses.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="don" className="mt-3">
            <Table>
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Notes</TableHead></TableRow></TableHeader>
              <TableBody>
                {profile.donations.length === 0 ? <TableRow><TableCell colSpan={3} className="text-center py-4 text-muted-foreground">No donations</TableCell></TableRow> :
                  profile.donations.map((d) => (
                    <TableRow key={d.id}><TableCell>{formatDate(d.donation_date)}</TableCell><TableCell className="text-right text-green-600 font-medium">{formatCurrency(d.amount)}</TableCell><TableCell className="text-xs">{d.notes || '—'}</TableCell></TableRow>
                  ))
                }
              </TableBody>
            </Table>
          </TabsContent>
          <TabsContent value="exp" className="mt-3">
            <Table>
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Category</TableHead><TableHead>Vendor</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
              <TableBody>
                {(profile.allocations.length + profile.legacy_expenses.length) === 0 ? <TableRow><TableCell colSpan={4} className="text-center py-4 text-muted-foreground">No expenses</TableCell></TableRow> : (
                  <>
                    {profile.allocations.map((a) => (
                      <TableRow key={a.id}><TableCell>{formatDate(a.expense_date)}</TableCell><TableCell>{a.category_name || '—'}</TableCell><TableCell>{a.vendor || '—'}</TableCell><TableCell className="text-right text-red-600 font-medium">{formatCurrency(a.allocated_amount)}</TableCell></TableRow>
                    ))}
                    {profile.legacy_expenses.map((e) => (
                      <TableRow key={e.id}><TableCell>{formatDate(e.date)}</TableCell><TableCell>{e.expense_type || '—'}</TableCell><TableCell className="text-muted-foreground">legacy</TableCell><TableCell className="text-right text-red-600 font-medium">{formatCurrency(e.amount)}</TableCell></TableRow>
                    ))}
                  </>
                )}
              </TableBody>
            </Table>
          </TabsContent>
          <TabsContent value="plan" className="mt-3">
            <Table>
              <TableHeader><TableRow><TableHead>Expected</TableHead><TableHead>Category</TableHead><TableHead className="text-right">Estimate</TableHead><TableHead>Notes</TableHead></TableRow></TableHeader>
              <TableBody>
                {profile.planned_expenses.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center py-4 text-muted-foreground">No planned items</TableCell></TableRow> :
                  profile.planned_expenses.map((p) => (
                    <TableRow key={p.id}><TableCell>{p.expected_date ? formatDate(p.expected_date) : '—'}</TableCell><TableCell>{p.category_name || '—'}</TableCell><TableCell className="text-right text-amber-600">{formatCurrency(p.estimated_amount)}</TableCell><TableCell className="text-xs">{p.notes || '—'}</TableCell></TableRow>
                  ))
                }
              </TableBody>
            </Table>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default SponsorshipAccountabilityPage;
